import {
  ApolloCache,
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  InMemoryCacheConfig,
  NormalizedCacheObject,
} from '@apollo/client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { setContext } from '@apollo/client/link/context';
import { getMainDefinition } from '@apollo/client/utilities';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { createClient } from 'graphql-ws';
import omitDeep from 'omit-deep-lodash';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { getAuthState } from '@cnx-saas/react-auth-state';

interface AssumedApolloContext {
  headers?: Record<string, string>;
}
const defaultCacheConfig: InMemoryCacheConfig = {
  typePolicies: {
    Query: {
      fields: {},
    },
  },
};

export class ApiClient {
  private readonly apiClient: ApolloClient<NormalizedCacheObject>;

  private constructor(
    endpoint: string,
    cache: ApolloCache<NormalizedCacheObject>,
    wssEndpoint?: string,
  ) {
    const httpLink = createUploadLink({
      uri: endpoint,
    });
    const authLink = setContext((_, { headers }: AssumedApolloContext) => {
      return {
        headers: {
          ...headers,
          Authorization: `Bearer ${getAuthState()?.token}`,
        },
      };
    }).concat(httpLink);

    let splitLink: ApolloLink | undefined;
    if (wssEndpoint) {
      const wssLink = new GraphQLWsLink(
        createClient({
          url: wssEndpoint,
          connectionParams: () => ({
            authToken: getAuthState()?.token,
          }),
        }),
      );
      splitLink = ApolloLink.split(
        ({ query }) => {
          const definition = getMainDefinition(query);
          return (
            definition.kind === 'OperationDefinition' &&
            definition.operation === 'subscription'
          );
        },
        wssLink,
        authLink,
      );
    }

    const cleanTypeName = new ApolloLink((operation, forward) => {
      if (operation.variables) {
        operation.variables = omitDeep(operation.variables, '__typename');
      }
      return forward(operation).map((data) => {
        return data;
      });
    });

    const linkChain = cleanTypeName.concat(splitLink ?? authLink);

    this.apiClient = new ApolloClient<NormalizedCacheObject>({
      link: linkChain,
      cache,
    });
  }

  public static create(
    endpoint: string,
    cacheConfig?: InMemoryCacheConfig,
    wssEndpoint?: string,
  ): ApiClient {
    const cache = new InMemoryCache(cacheConfig ?? defaultCacheConfig);

    return new ApiClient(endpoint, cache, wssEndpoint);
  }

  public getClient(): ApolloClient<NormalizedCacheObject> {
    return this.apiClient;
  }
}
